import React, { useEffect } from "react";
import FlightCard from "@tripbuilder/cat-overview/right/FlightCard";
import DayViewComponent from "@tripbuilder/cat-overview/right/DayView";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useLocationData } from "@/hooks/useLocations";
import { Location } from "@/types/trip";
import useToraStore from "@/stores/tripBuilder/useToraStore";
import { useToraStreamContent } from "@/hooks/useToraStreamContent";
import ToraRecommendations from "@tripbuilder/cat-tora/right/ToraRecommendations";
import { AnimatePresence, motion } from "framer-motion";
import { MapPinIcon } from "@heroicons/react/24/outline";

interface OverviewTabProps {
  switchToSchedule: () => void;
  handleTransportButtonClick: (location: Location) => void;
  setIsModalOpen: (isOpen: boolean) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  switchToSchedule,
  handleTransportButtonClick,
  setIsModalOpen,
}) => {
  const { getItineraryItems, tripData, selectedLocation } = useTripStore();
  const { previousLocation, nextLocation, isLastLocation } = useLocationData();
  const { showRecommendations, showAccommodations, setCurrentLocationId } =
    useToraStore();

  useEffect(() => {
    setCurrentLocationId(selectedLocation?.id || "");
  }, [selectedLocation?.id, setCurrentLocationId]);

  const itineraryItems = getItineraryItems();

  const flights = tripData?.transport;
  const flightsFromPreviousLocation =
    flights?.filter(
      (transport) => transport.fromLocationId === previousLocation?.id
    )[0] || null;
  const flightsToNextLocation = !isLastLocation
    ? flights?.filter(
        (transport) => transport.toLocationId === nextLocation?.id
      )[0] || null
    : null;

  return (
    <div
      id="rhs-overview"
      className={`relative my-2 py-4 w-full h-full flex flex-col items-center justify-start rounded-lg ${
        showRecommendations || showAccommodations
          ? ""
          : "overflow-y-scroll no-scrollbar"
      }`}
      data-tutorial="overview-total-schedule"
    >
      <AnimatePresence mode="wait">
        {showRecommendations || showAccommodations ? (
          <motion.div
            key="recommendations"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <ToraRecommendations />
          </motion.div>
        ) : (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <FlightCard
              previousLocation={previousLocation}
              nextLocation={nextLocation}
              selectedLocation={selectedLocation}
              flight={flightsFromPreviousLocation}
              handleTransportButtonClick={handleTransportButtonClick}
              arrival={true}
            />
            <div className="my-4 w-full flex flex-col items-center justify-start space-y-4">
              {itineraryItems?.map((day: any, index: number) => (
                <DayViewComponent
                  key={`${day.date}-${index}`}
                  day={index + 1}
                  date={day.date}
                  switchToSchedule={switchToSchedule}
                  handleTransportButtonClick={handleTransportButtonClick}
                />
              ))}
            </div>

            {isLastLocation ? (
              <div className="bg-neutral-100/70 rounded-lg py-3 px-4 w-full">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center space-x-4">
                    <MapPinIcon className="w-5 h-5 text-neutral-600" />
                    <div className="flex flex-col items-start">
                      <p className="text-sm text-neutral-700 font-medium mb-0.5">
                        Where to next?
                      </p>
                      <p className="text-xs text-neutral-500 font-light">
                        Add another destination
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="h-8 w-8 flex items-center justify-center bg-indigo-800 rounded-md"
                  >
                    <i className="fa-solid fa-plus text-white text-xs"></i>
                  </button>
                </div>
              </div>
            ) : (
              <FlightCard
                previousLocation={previousLocation}
                nextLocation={nextLocation}
                selectedLocation={selectedLocation}
                flight={flightsToNextLocation}
                handleTransportButtonClick={handleTransportButtonClick}
                arrival={false}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default React.memo(OverviewTab);
